import { Injectable } from '@angular/core';
import { CrudService } from '../shared/grid/crud.service';
import { SegOrgLevel, SegOrgLevelRecordHistory } from '../models/segOrgLevel.model';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { GridModel } from '../shared/grid/grid.model';
import nameOf from '../shared/form/nameOf.helper';
import { buildHistoryParams } from '../_helpers/history-popup.helper';

export interface SegOrgLevelBulkAddRequest {
  segIds: string[];
  orgLevelIds: string[];
}

export interface SegOrgLevelBulkAddResult {
  segId: string;
  organizationLevelId: string;
  skipped?: boolean;
  success?: boolean;
  error?: string;
}

export interface SegOrgLevelBulkPatchRow {
  segId: string;
  orgLevelId: string;
  shiftId?: string | null;
  numberOfWorkers?: number | null;
  numberOfContractors?: number | null;
  shiftLengthHours?: number | null;
  shiftType?: number | null;
  avgWeeklyHours?: number | null;
  clearFields?: string[];
}

export interface SegOrgLevelBulkPatchRequest {
  rows: SegOrgLevelBulkPatchRow[];
}

interface LegacySegOrgLevelKey {
  segId: string;
  orgLevelId: string;
}

interface LegacyBulkEditSegOrgLevelsRequest {
  keys: LegacySegOrgLevelKey[];
  numberOfWorkers?: number | null;
  numberOfContractors?: number | null;
  shiftLengthHours?: number | null;
  avgWeeklyHours?: number | null;
}

type LegacyBulkEditUpdate = Omit<LegacyBulkEditSegOrgLevelsRequest, 'keys'>;

export enum SegOrgLevelPatchErrorCode {
  None = 0,
  NotFound = 1,
  PermissionDenied = 2,
  ValidationFailed = 3,
  Conflict = 4,
}

export interface SegOrgLevelBulkPatchResult {
  segId: string;
  orgLevelId: string;
  success?: boolean;
  error?: string;
  errorCode?: SegOrgLevelPatchErrorCode;
  shiftId?: string | null;
  shiftLengthHours?: number | null;
  avgWeeklyHours?: number | null;
  numberOfWorkers?: number | null;
  numberOfContractors?: number | null;
}

export interface SegOrgLevelBulkPatchResponse {
  successCount?: number;
  failureCount?: number;
  results?: SegOrgLevelBulkPatchResult[];
}

@Injectable({
  providedIn: 'root'
})
export class SegOrgLevelService extends CrudService<SegOrgLevel> {

  constructor(http: HttpClient) {
    super('SegOrgLevel', http)
  }

  getBySegId(segId: string, orgLevelId: string): Observable<SegOrgLevel> {
    const params = new HttpParams()
      .set('segId', segId.toString())
      .set('organizationLevelId', orgLevelId.toString());

    return this.http.get<SegOrgLevel>(
      this.getApiUrl(`${this.controllerName}/Get`), { params }
    );
  }

  // DASH-3095: getAllBySegOrgLevelId (GET /SegOrgLevel/GetAll) was removed. It omitted the now
  // BindRequired organizationLevelId query param, so the endpoint always returned []. All callers
  // now use getAssociations, which supports an optional SEG and/or parent-org-level filter and
  // applies the org-level security scope server-side.

  getAssociations(segId?: string, parentOrgLevelId?: string, orgLevelType?: number): Observable<SegOrgLevel[]> {
    let params = new HttpParams();
    if (segId !== undefined && segId !== null) {
      params = params.set('segId', segId.toString());
    }
    if (parentOrgLevelId !== undefined && parentOrgLevelId !== null) {
      params = params.set('parentOrgLevelId', parentOrgLevelId.toString());
    } else if (orgLevelType !== undefined && orgLevelType !== null) {
      params = params.set('orgLevelType', orgLevelType.toString());
    }

    return this.http.get<SegOrgLevel[]>(this.getApiUrl(`${this.controllerName}/GetAssociations`), { params });
  }

  deleteById(segId: string, orgLevelId: string): Observable<SegOrgLevel> {
    const params = new HttpParams()
      .set('segId', segId.toString())
      .set('organizationLevelId', orgLevelId.toString());

    return this.http.delete<SegOrgLevel>(
      this.getApiUrl(`${this.controllerName}/Delete`), { params }
    );
  }

  getHistory(segId: string, entityId?: string | null, page = 0): Observable<GridModel<SegOrgLevelRecordHistory>> {
    let params = buildHistoryParams(nameOf<SegOrgLevelRecordHistory>(x => x.segId), segId, page);
    if (entityId) {
      params = params.set('filterBy', JSON.stringify({ [nameOf<SegOrgLevelRecordHistory>(x => x.entityId)]: entityId }));
    }

    return this.http.get<GridModel<SegOrgLevelRecordHistory>>(
      this.getApiUrl(`${this.controllerName}/GetHistory`), { params }
    );
  }

  bulkAddSegOrgLevels(payload: SegOrgLevelBulkAddRequest): Observable<SegOrgLevelBulkAddResult[]> {
    return this.http.post<SegOrgLevelBulkAddResult[]>(
      this.getApiUrl(`${this.controllerName}/BulkAddSegOrgLevels`),
      payload,
    );
  }

  bulkPatch(payload: SegOrgLevelBulkPatchRequest): Observable<SegOrgLevelBulkPatchResponse> {
    return this.http.patch<SegOrgLevelBulkPatchResponse>(
      this.getApiUrl(`${this.controllerName}/BulkPatch`),
      payload,
    ).pipe(
      catchError((patchError: HttpErrorResponse) => {
        if (patchError.status !== 404 && patchError.status !== 405) {
          return throwError(() => patchError);
        }

        // BulkPatch endpoint not yet deployed — fall back to the legacy endpoint.
        const legacyPayload = this.buildLegacyBulkEditPayload(payload);
        if (!legacyPayload) {
          // shiftId operations require the new BulkPatch endpoint and cannot use
          // the legacy BulkEditSegOrgLevels endpoint. Return a per-row failure
          // response so the component shows a proper cell error instead of
          // propagating the raw HTTP 404 message.
          return of(this.buildUnsupportedOperationResponse(payload));
        }

        return this.http.post<SegOrgLevelBulkPatchResponse>(
          this.getApiUrl(`${this.controllerName}/BulkEditSegOrgLevels`),
          legacyPayload,
        );
      }),
    );
  }

  private buildUnsupportedOperationResponse(payload: SegOrgLevelBulkPatchRequest): SegOrgLevelBulkPatchResponse {
    return {
      successCount: 0,
      failureCount: payload.rows.length,
      results: payload.rows.map((row) => ({
        segId: row.segId,
        orgLevelId: row.orgLevelId,
        success: false,
        errorCode: SegOrgLevelPatchErrorCode.ValidationFailed,
      })),
    };
  }

  private buildLegacyBulkEditPayload(payload: SegOrgLevelBulkPatchRequest): LegacyBulkEditSegOrgLevelsRequest | null {
    if (!payload.rows.length) {
      return null;
    }

    const firstUpdate = this.toLegacyBulkEditUpdate(payload.rows[0]);
    if (!firstUpdate) {
      return null;
    }

    const allRowsCompatible = payload.rows.every((row) => {
      const update = this.toLegacyBulkEditUpdate(row);
      return update && this.areLegacyUpdatesEqual(update, firstUpdate);
    });

    if (!allRowsCompatible) {
      return null;
    }

    return {
      keys: payload.rows.map((row) => ({ segId: row.segId, orgLevelId: row.orgLevelId })),
      ...firstUpdate,
    };
  }

  private toLegacyBulkEditUpdate(row: SegOrgLevelBulkPatchRow): LegacyBulkEditUpdate | null {
    if (row.shiftId !== undefined || row.shiftType !== undefined) {
      return null;
    }

    const clearFields = row.clearFields ?? [];
    let hasInvalidClearField = false;

    const isCleared = (field: string): boolean =>
      clearFields.some((value) => value.toLowerCase() === field.toLowerCase());

    const update: LegacyBulkEditUpdate = {};

    const resolveUpdateValue = (value: number | null | undefined, field: string): number | null | undefined =>
      value ?? (isCleared(field) ? null : undefined);

    const numberOfWorkers = resolveUpdateValue(row.numberOfWorkers, 'numberOfWorkers');
    if (numberOfWorkers !== undefined) {
      update.numberOfWorkers = numberOfWorkers;
    }

    const numberOfContractors = resolveUpdateValue(row.numberOfContractors, 'numberOfContractors');
    if (numberOfContractors !== undefined) {
      update.numberOfContractors = numberOfContractors;
    }

    const shiftLengthHours = resolveUpdateValue(row.shiftLengthHours, 'shiftLengthHours');
    if (shiftLengthHours !== undefined) {
      update.shiftLengthHours = shiftLengthHours;
    }

    const avgWeeklyHours = resolveUpdateValue(row.avgWeeklyHours, 'avgWeeklyHours');
    if (avgWeeklyHours !== undefined) {
      update.avgWeeklyHours = avgWeeklyHours;
    }

    clearFields.forEach((field) => {
      const normalizedField = field.toLowerCase();
      if (
        normalizedField !== 'numberofworkers' &&
        normalizedField !== 'numberofcontractors' &&
        normalizedField !== 'shiftlengthhours' &&
        normalizedField !== 'avgweeklyhours'
      ) {
        hasInvalidClearField = true;
      }
    });

    const hasKnownUpdate =
      update.numberOfWorkers !== undefined ||
      update.numberOfContractors !== undefined ||
      update.shiftLengthHours !== undefined ||
      update.avgWeeklyHours !== undefined;

    if (hasInvalidClearField || !hasKnownUpdate) {
      return null;
    }

    return update;
  }

  private areLegacyUpdatesEqual(left: LegacyBulkEditUpdate, right: LegacyBulkEditUpdate): boolean {
    return left.numberOfWorkers === right.numberOfWorkers &&
      left.numberOfContractors === right.numberOfContractors &&
      left.shiftLengthHours === right.shiftLengthHours &&
      left.avgWeeklyHours === right.avgWeeklyHours;
  }
}
